<template>
  <div v-if="hasNativeShare" class="native-share">
    <button class="btn btn--big native-share-btn" @click="showNativeShare">
      {{ shareAction }}
      <small class="subtitle" v-if="shareActionSubtitle"
        ><br />{{ shareActionSubtitle }}</small
      >
    </button>
  </div>
  <div class="social-share" v-else>
    <div class="social-share__links" :class="{ mini: inline, inline: inline }">
      <a
        class="social-share__link social-share__link--whatsapp social-share__link--mobile"
        :href="whatsAppShareLink"
        @click="whatsAppShareHandler"
        target="_blank"
        ><span class="social-share__link-icon"
          ><WhatsAppIcon width="27px" height="27px" aria-role="presentation" />
        </span>
        <span class="social-share__link-text" v-if="!inline">WhatsApp</span>
      </a>
      <a
        class="social-share__link social-share__link--whatsapp social-share__link--desktop"
        :href="whatsAppShareLinkDesktop"
        @click="whatsAppShareHandler"
        target="_blank"
        ><span class="social-share__link-icon"
          ><WhatsAppIcon width="27px" height="27px" aria-role="presentation"
        /></span>
        <span class="social-share__link-text" v-if="!inline">WhatsApp</span>
      </a>
      <a
        class="social-share__link social-share__link--email"
        :href="emailShareLink"
        @click="emailShareHandler"
        target="_blank"
        ><span class="social-share__link-icon">
          <EnvelopeIcon width="25px" height="19px" aria-role="presentation" />
        </span>
        <span class="social-share__link-text" v-if="!inline">Email</span>
      </a>
    </div>

    <form class="form social-share__form" :class="{ inline: inline }">
      <fieldset class="form__fieldset">
        <div class="form__inline">
          <input
            type="url"
            class="form__input"
            id="share-link"
            data-testid="share-link"
            name="share-link"
            v-model="shareLink"
            readonly
          />
          <button
            type="button"
            class="btn share-link__copy"
            data-clipboard-target="#share-link"
            ref="copyBtn"
            @click="copyShareLinkHandler"
          >
            Copy Link
          </button>
        </div>
      </fieldset>
    </form>
  </div>
</template>

<script>
import ClipboardJS from "clipboard";
import WhatsAppIcon from "@/assets/svg/logo-whatsapp.svg";
import EnvelopeIcon from "@/assets/svg/icon-envelope.svg";
import { nativeShare, canUseNativeShare } from "@/utility/nativeShare";

export default {
  name: "SocialShare",
  props: {
    addressLine1: {
      type: String,
      required: true,
    },
    shareAction: {
      type: String,
      required: true,
    },
    shareActionSubtitle: {
      type: String,
      required: false,
    },
    shareLink: {
      type: String,
      required: false,
    },
    shareSubject: {
      type: String,
      required: true,
    },
    shareBody: {
      type: String,
      required: true,
    },
    emailViaModal: {
      type: Boolean,
      default: false,
    },
    whatsAppShareBody: {
      type: String,
      required: true,
    },
    inline: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isShareInProgress: false,
    };
  },
  computed: {
    whatsAppShareLink() {
      return `https://api.whatsapp.com/send?text=${encodeURIComponent(
        this.whatsAppShareBody,
      )}${encodeURIComponent(this.shareLink)}`;
    },
    whatsAppShareLinkDesktop() {
      return `https://web.whatsapp.com/send?text=${encodeURIComponent(
        this.whatsAppShareBody,
      )}${encodeURIComponent(this.shareLink)}`;
    },
    emailShareLink() {
      return `mailto:?subject=${this.shareSubject}${encodeURIComponent(
        this.addressLine1,
      )}&body=${encodeURIComponent(this.shareBody)}`;
    },
    hasNativeShare() {
      return canUseNativeShare();
    },
  },
  methods: {
    whatsAppShareHandler() {
      this.$emit("whatsAppShare");
    },
    emailShareHandler(event) {
      this.$emit("emailShare");

      if (this.emailViaModal) {
        event.preventDefault();

        this.$emit("show-modal");

        return false;
      } else {
        return true;
      }
    },
    copyShareLinkHandler() {
      this.$emit("copyShareLink");
    },
    showNativeShare() {
      if (!this.isShareInProgress) {
        this.isShareInProgress = true;

        nativeShare({
          title: this.shareSubject + this.addressLine1,
          text: this.shareBody,
          //url: this.shareLink
        }).finally(() => {
          this.isShareInProgress = false;
        });
      }
    },
  },
  mounted() {
    if (!canUseNativeShare()) {
      this.clipboard = new ClipboardJS(this.$refs.copyBtn);
    }
  },
  beforeDestroy() {
    if (this.clipboard) {
      this.clipboard.destroy();
    }
  },
  components: {
    WhatsAppIcon,
    EnvelopeIcon,
  },
};
</script>

<style lang="scss" scoped>
.native-share {
  margin-top: $spacing-small;

  .native-share-btn {
    padding: $spacing-small;
    background-color: $brand-dark-blue;

    &:hover {
      background-color: darken($brand-dark-blue, 5%);
    }

    @include media("<medium") {
      font-size: 20px;
    }
  }

  .share-icon {
    margin-right: 1rem;
    display: inline;
  }
}

.social-share {
  display: flex;
  flex-direction: column;
  margin-bottom: $spacing-medium;
}

.social-share__link {
  display: flex;
  min-width: 230px;
  text-decoration: none;
  text-align: left;
  font-size: 1.6rem;
  font-weight: 100;
  margin-bottom: $spacing-medium;

  &:last-child {
    margin-bottom: 0;
  }

  &.social-share__link--whatsapp {
    .social-share__link-icon {
      color: $white;
      background-color: #25d366;
    }

    .social-share__link-text {
      color: $white;
      background-color: #25d366;
    }
  }

  &.social-share__link--messenger {
    .social-share__link-icon {
      color: $white;
      background-color: #4267b2;
    }

    .social-share__link-text {
      color: $white;
      background-color: #4267b2;
    }
  }

  &.social-share__link--email {
    .social-share__link-icon {
      background: $grey-faint;

      &.inline {
        background: white;
      }

      color: $black;
    }
  }

  &.social-share__link--mobile {
    @include media(">=medium") {
      display: none;
    }
  }

  &.social-share__link--desktop {
    @include media("<medium") {
      display: none;
    }
  }
}

.social-share__links {
  &.inline {
    order: 2;
    max-width: 20rem;
    margin: 0 auto;

    .social-share__link-icon {
      box-shadow: $brand-box-shadow;
    }
  }

  &.mini {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    width: 100%;

    .social-share__link {
      min-width: auto;
      margin-bottom: 0;
    }

    .social-share__link-icon {
      margin-right: 0;
    }
  }
}

.social-share__link-icon {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 42px;
  height: 42px;
  border-radius: $border-radius-xs;
  padding: 5px;
  margin-right: $spacing-small;

  flex-shrink: 0;
}

.social-share__link-text {
  flex-basis: 100%;

  background-color: $grey-faint;
  border-radius: $border-radius-xs;
  padding: 9px $spacing-medium;

  font-weight: $font-regular;
}

.social-share__form {
  margin-top: $spacing-medium;

  .form__fieldset {
    border: 1px solid $grey-mid;
    border-radius: $border-radius-xs;
  }

  &.inline {
    order: 1;
  }

  input {
    padding: 0;
    border: none;
    background: none;
    flex-shrink: 1;
    margin: 0 $spacing-small;

    &:focus {
      outline: none;
    }
  }

  button {
    font-size: 16px;
    flex-shrink: 0;
    background: $brand-orange;
    color: $white;
    padding: $spacing-tiny $spacing-xs;
    margin: $spacing-tiny $spacing-tiny $spacing-tiny 0;
    border: none;
    border-radius: $border-radius-xs;

    &:focus {
      outline: none;
    }
  }
}
</style>
